import type { AvailabilityRun, GroupAvailabilityMember } from 'components/TrainingPlanner/types';

/**
 * Whether one merged set of free stretches contains a window whole.
 *
 * Exported because the availability matrix asks the same question of a whole
 * pool, cell by cell. One predicate, so "can train then" cannot come to mean
 * two slightly different things in two places — the runs are already merged
 * and non-overlapping, which is what makes a single containment check the
 * complete answer.
 */
/**
 * The least a stretch has to be for the question below to have an answer.
 *
 * Widened from `AvailabilityRun` so a court's opening hours can be asked the
 * same question as a person's stated times: both are weekly stretches in
 * minutes, and the weight a person's stretches also carry is not read here.
 */
export interface WeeklyStretch {
    weekday: number;
    start: number;
    end: number;
}

export const coversWindow = (
    runs: readonly WeeklyStretch[],
    weekday: number,
    startMinute: number,
    endMinute: number
): boolean => runs.some((run) => run.weekday === weekday && run.start <= startMinute && run.end >= endMinute);

const covers = coversWindow;

/**
 * Whether a candidate window falls outside the group's common availability.
 *
 * `common` is already the intersection of every known member's own stretches
 * (see `TrainingPlannerWeekController::commonAvailability`), so containment
 * in one of its entries is exactly "every known member covers this window
 * individually" — the same thing `MemberConflictService` decides per person
 * after a drop, just answerable here as one lookup instead of an iteration,
 * which is what a live drag preview needs on every pointer frame.
 *
 * Null `common` means nobody on the roster has stated anything: a gap in the
 * data, not a group free of nobody, so nothing is warned about.
 */
export const isOutsideAvailability = (
    common: AvailabilityRun[] | null | undefined,
    weekday: number,
    startMinute: number,
    endMinute: number
): boolean => {
    if (!common || endMinute <= startMinute) return false;

    return !covers(common, weekday, startMinute, endMinute);
};

/**
 * How much of a group an hour has to miss before it is called a conflict.
 *
 * A club plans around what its participants asked for; it cannot honour all
 * of it at once. On a roster of ten there is nearly always somebody whose
 * stated times stop an hour before the only free court, and warning about
 * every one of them turned the week into a wall of triangles — a warning
 * standing on almost every appointment is not read as a warning any more.
 *
 * Two fifths is where "somebody cannot make it" turns into "this is the
 * wrong hour for this group". Below it the miss is still shown — the drag
 * preview names who, the participant list keeps its own per-person marks —
 * it just stops speaking in the voice reserved for a double-booked court.
 *
 * Deliberately a lower bar than `MIN_COVERAGE_RATIO` above, because the two
 * answer different questions: half the group is what it takes to *offer* an
 * hour, two fifths is what it takes to *object* to one.
 *
 * The server applies the same fraction to the appointments already in the
 * week (`TrainingPlannerWeekController::CONFLICT_SHARE`); this is the live
 * half of it, for the hour that only exists under the cursor.
 */
export const MIN_CONFLICT_RATIO = 0.4;

/** Whether a miss this size, on a roster this size, is worth a warning. */
export const missesTooMany = (missing: number, roster: number): boolean =>
    missing > 0 && roster > 0 && missing / roster >= MIN_CONFLICT_RATIO;

/**
 * One stretch of a day, and how much of the roster it works for.
 *
 * `count`/`total` are absent while only the intersection is known — the
 * stretch is then one everybody can make, it just cannot say how many that
 * is. See `buildCoverageBands` for where the counted version comes from.
 */
export interface CoverageBand {
    weekday: number;
    start: number;
    end: number;
    count?: number;
    total?: number;
    /**
     * Who is not free in this stretch. A count says how big the compromise
     * is, but not whether it is one worth making — leaving out the member
     * who already trains twice a week is a different decision from leaving
     * out the one who does not, and only a name can tell them apart.
     */
    missing?: string[];
}

/**
 * At least half the group, or the band is not drawn.
 *
 * Without a floor every hour one member happens to be free becomes a band,
 * and a roster of eight paints most of the week. The question a planner is
 * asking while dragging is "could this group meet here" — an hour that works
 * for two of eight is not a worse answer to that, it is not an answer.
 */
const MIN_COVERAGE_RATIO = 0.5;

/**
 * The whole week broken into stretches, each labelled with how many of the
 * roster can train in it.
 *
 * The intersection alone only ever says "everybody" or nothing, which hides
 * the compromise a planner most wants to see: the hour four of the five can
 * make. This splits the week at every point where somebody's availability
 * starts or ends, counts the roster over each resulting piece, and merges
 * neighbours that came out the same — the same sweep
 * `AvailabilityCalculator::canonicalizeDay` does server-side, counting
 * members instead of resolving weights.
 *
 * Each member's own runs are already merged and non-overlapping, so at most
 * one of them can cover any given piece: counting runs is counting people.
 */
export const buildCoverageBands = (members: GroupAvailabilityMember[] | undefined): CoverageBand[] => {
    if (!members || members.length === 0) return [];

    const total = members.length;
    const runsByWeekday = new Map<number, AvailabilityRun[]>();
    members.forEach((member) =>
        member.runs.forEach((run) => {
            const list = runsByWeekday.get(run.weekday);
            if (list) list.push(run);
            else runsByWeekday.set(run.weekday, [run]);
        })
    );

    const bands: CoverageBand[] = [];

    runsByWeekday.forEach((runs, weekday) => {
        const points = [...new Set(runs.flatMap((run) => [run.start, run.end]))].sort((a, b) => a - b);
        let open: CoverageBand | null = null;

        for (let index = 0; index < points.length - 1; index += 1) {
            const start = points[index];
            const end = points[index + 1];
            const missing = members
                .filter(
                    (member) =>
                        !member.runs.some((run) => run.weekday === weekday && run.start <= start && run.end >= end)
                )
                .map((member) => member.name);
            const count = total - missing.length;

            if (count < total * MIN_COVERAGE_RATIO) {
                open = null;
                continue;
            }

            // Extended rather than appended whenever the previous piece ran up
            // to this one and counted the same, so a member's own interval
            // boundaries never cut a band that is uniform across them. Equal
            // counts are not enough to merge on — four of five available can
            // be a different four either side of a boundary, and merging them
            // would name the wrong people for half the band.
            const sameMissing =
                open?.missing?.length === missing.length &&
                (open?.missing ?? []).every((name, at) => name === missing[at]);

            if (open && open.end === start && sameMissing) open.end = end;
            else {
                open = { weekday, start, end, count, total, missing };
                bands.push(open);
            }
        }
    });

    return bands;
};

/**
 * Which members cannot attend a candidate window, by name.
 *
 * The intersection above answers *whether* a window works and is enough to
 * colour a preview, but "nobody can make this" and "Lena cannot make this"
 * lead to completely different decisions, and only one of them is worth
 * abandoning a drag over. This is the second question, and it needs the
 * per-member stretches the group availability endpoint returns — which is
 * why it is fetched once when a drag starts rather than shipped with every
 * appointment in the week.
 *
 * Returns an empty list when the roster is unknown, mirroring
 * `isOutsideAvailability`: silence in the data is never reported as a
 * problem with the plan — which is why a member who has stated nothing at
 * all is skipped rather than counted as unable. An empty set of runs covers
 * no window, so without that skip the one rule the whole module agrees on
 * ("nothing stated is a gap in the data, not a conflict with it", see
 * `MemberConflictService::outsideAvailability`) would be broken by exactly
 * the people it was written for.
 */
export const whoCannotAttend = (
    members: GroupAvailabilityMember[] | undefined,
    weekday: number,
    startMinute: number,
    endMinute: number
): string[] => {
    if (!members || endMinute <= startMinute) return [];

    return members
        .filter((member) => member.runs.length > 0 && !covers(member.runs, weekday, startMinute, endMinute))
        .map((member) => member.name);
};
